import { useEffect, useState, useRef } from 'react';
import { Box, keyframes } from '@mui/material';
// @ts-ignore
import { svgAsDataUri } from 'save-svg-as-png';

import { getAppThemeColor } from '../../helpers/themeHelper';
import NuiCircularProgress from '../NuiCircularProgress';
import { globals } from '../../theme';

const animArtwork = keyframes`
  0% {
    opacity: 0;
    transform: translateY(1rem);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const animOverlay = keyframes`
  0% {
    opacity: 0.2;
  }
  50% {
    opacity: 0.1;
  }
  100% {
    opacity: 0.2;
  }
`;

const animImage = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

interface ArtworkProps {
  loading: boolean;
  artwork?: ArtworkTypes;
  onArtwork: Function;
}

const Artwork: React.FC<ArtworkProps> = ({ loading, artwork, onArtwork }) => {
  const artworkRef = useRef(null);
  const [color, setColor] = useState(getAppThemeColor());

  // Set loading colour
  useEffect(() => {
    if (artwork) return;
    setColor(getAppThemeColor());
  }, [artwork]);

  // Handle rendered artwork conversion
  // From SVG to base64 PNG
  useEffect(() => {
    if (!artworkRef.current) return;
    const getPng = async () => {
      const res = await svgAsDataUri(artworkRef.current, { scale: 0.5 });
      onArtwork(res);
    };
    getPng();
  }, [artworkRef, onArtwork]);

  // Handle error and hide
  const isError = !loading && !artwork;
  if (isError) return null;

  const data = artwork?.data;
  const colors = artwork?.colors;
  const textColor = artwork?.colors?.invert
    ? globals.colors.black
    : globals.colors.white;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '75%',
        aspectRatio: '3/2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        pointerEvents: 'none',
        opacity: 0,
        animation: `${animArtwork} 1s forwards`,
      }}>
      {/* Loading image overlay */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 1,
          overflow: 'hidden',
        }}>
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backgroundColor: color,
            animation: `${animOverlay} 3s linear infinite`,
          }}
        />
        {/* Spinner */}
        <NuiCircularProgress
          size={24}
          sx={{
            '&.MuiCircularProgress-colorPrimary': {
              color,
            },
          }}
        />
      </Box>

      {/* Loaded artwork */}
      {!loading && data && (
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            overflow: 'hidden',
            opacity: 0,
            animation: `${animImage} 0.3s linear forwards`,
            img: {
              position: 'absolute',
              right: 0,
              width: '80%',
              height: 'auto',
            },
          }}>
          <svg
            ref={artworkRef}
            width="100%"
            height="800"
            viewBox="0 0 1200 800"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            style={{ width: '100%', height: 'auto' }}>
            {/* Bakground */}
            <rect width="1200" height="800" fill={globals.colors.black} />
            {/* Image */}
            <image
              width="960"
              height="960"
              transform="translate(240 -80)"
              xlinkHref={data}
            />
            {/* Spine */}
            <rect width="240" height="800" fill={colors?.primary} />
            {/* Logo */}
            <path
              d="M153.906 55.5175C153.028 55.5168 152.15 55.5718 151.278 55.6823H151.103L150.99 55.5429C147.343 50.7099 142.644 46.7935 137.26 44.0979C131.876 41.4023 125.95 40 119.944 40C113.937 40 108.012 41.4023 102.627 44.0979C97.243 46.7935 92.5447 50.7099 88.8974 55.5429L88.7973 55.6823H88.622C87.7459 55.5722 86.864 55.5171 85.9811 55.5175H80.6993V65.4425L80.5241 65.5312C77.9755 66.744 75.8206 68.6664 74.3111 71.0738C72.8016 73.4812 72 76.274 72 79.1256C72 81.9775 72.8016 84.7701 74.3111 87.1774C75.8206 89.5848 77.9755 91.5074 80.5241 92.7202L80.6993 92.7963V92.999C80.6993 103.555 84.8399 113.678 92.2102 121.143C99.5805 128.607 109.577 132.8 120 132.8C130.423 132.8 140.42 128.607 147.79 121.143C155.16 113.678 159.301 103.555 159.301 92.999V92.7963L159.476 92.7202C162.025 91.5074 164.179 89.5848 165.689 87.1774C167.198 84.7701 168 81.9775 168 79.1256C168 76.274 167.198 73.4812 165.689 71.0738C164.179 68.6664 162.025 66.744 159.476 65.5312L159.301 65.4425V55.5175H153.906ZM99.1857 58.7625C101.903 55.984 105.137 53.7784 108.701 52.2731C112.265 50.7679 116.089 49.9929 119.95 49.9929C123.811 49.9929 127.635 50.7679 131.199 52.2731C134.763 53.7784 137.997 55.984 140.714 58.7625L141.002 59.0667L140.639 59.2695C139.145 60.1 137.71 61.0361 136.346 62.0708L136.184 62.1849L136.021 62.0961C131.043 59.5123 125.543 58.1284 119.95 58.0527C114.357 58.1284 108.857 59.5123 103.879 62.0961L103.704 62.1849L103.554 62.0708C102.185 61.036 100.746 60.0999 99.2483 59.2695L98.8853 59.0287L99.1857 58.7625ZM148.562 92.6693C148.562 100.354 145.548 107.724 140.182 113.159C134.816 118.593 127.538 121.646 119.95 121.646C112.362 121.646 105.084 118.593 99.7183 113.159C94.3525 107.724 91.338 100.354 91.338 92.6693V67.4832L91.726 67.5846C96.7325 68.8522 101.476 74.4167 102.578 75.7729C104.877 73.5156 107.598 71.7445 110.581 70.5628C113.563 69.3811 116.748 68.8125 119.95 68.8902C123.152 68.8125 126.337 69.3811 129.319 70.5628C132.302 71.7445 135.023 73.5156 137.322 75.7729C138.411 74.4167 143.117 68.8522 148.174 67.5846L148.562 67.4832V92.6693Z"
              fill={textColor}
            />
            {/* Napster */}
            <g fill={textColor}>
              <path d="M99.7211 620.067C99.7211 625.722 102.387 630.698 106.504 632.736L107.55 633.262L100.572 634.066V644.586H158.212V633.387H132.616L133.927 632.736C138.045 630.698 140.71 625.722 140.71 620.067C140.71 609.173 131.904 600.967 120.223 600.967C108.541 600.967 99.7211 609.173 99.7211 620.067ZM130.076 622.188C130.076 624.794 129.033 627.293 127.177 629.137C125.322 630.979 122.805 632.015 120.181 632.015C117.556 632.015 115.04 630.979 113.184 629.137C111.328 627.293 110.286 624.794 110.286 622.188C110.286 619.581 111.328 617.082 113.184 615.239C115.04 613.396 117.556 612.361 120.181 612.361C122.805 612.361 125.322 613.396 127.177 615.239C129.033 617.082 130.076 619.581 130.076 622.188Z" />
              <path d="M140.71 580.481C140.71 572.165 137.068 563.738 128.931 563.738C123.209 563.738 119.162 567.605 116.664 575.547C116.12 577.183 115.673 578.596 115.268 579.788C113.594 585 113.147 586.414 111.556 586.414C109.965 586.414 108.904 584.141 108.904 581.895C108.904 580.107 109.588 577.099 112.072 576.78V565.484C104.773 565.859 99.6929 572.706 99.6929 582.283C99.6929 592.291 105.806 597.682 111.556 597.682C117.306 597.682 121.2 593.829 123.502 585.582L124.214 582.99C125.889 576.961 126.447 574.937 128.666 574.937C130.885 574.937 131.457 577.792 131.457 580.246C131.457 584.321 129.978 587.176 127.647 587.619V598.846C135.979 598.458 140.71 591.778 140.71 580.481Z" />
              <path d="M110.286 470.76C110.313 469.53 110.578 468.316 111.067 467.184L101.172 465.7C100.231 466.926 99.7379 468.434 99.7767 469.972C99.7767 472.745 101.284 475.447 104.257 478.066L104.815 478.552L100.628 479.175V489.695H139.915V478.509H124.102C115.185 478.509 110.286 475.764 110.286 470.76Z" />
              <path d="M100.516 534.547V543.903H88.6258V555.103H100.516V561.714H111.081V555.103H139.915V543.903H111.081V534.547H100.516Z" />
              <path d="M120.767 494.172C108.569 494.172 99.7211 502.391 99.7211 513.729C99.7211 525.067 108.248 533.3 119.972 533.3C131.988 533.3 140.71 524.776 140.71 513.022C140.71 502.349 133.244 496.112 127.731 495.003L126.684 504.706C127.997 505.656 129.058 506.91 129.772 508.359C130.487 509.809 130.836 511.409 130.787 513.022C130.787 519.398 127.215 521.921 123.893 522.17H123.516V494.213L120.767 494.172ZM116.05 522.129H115.673C112.184 521.865 109.644 518.345 109.644 513.812C109.644 508.628 112.672 505.69 115.673 505.496H116.05V522.129Z" />
              <path d="M100.516 661.316L107.495 662.12L106.448 662.633C102.261 664.684 99.6651 669.563 99.6651 675.232C99.6651 686.168 108.472 694.401 120.167 694.401C131.862 694.401 140.654 686.168 140.654 675.232C140.654 669.688 137.989 664.684 133.872 662.633L132.825 662.12L139.803 661.316V650.782H100.516V661.316ZM130.076 673.194C130.07 675.139 129.484 677.037 128.392 678.651C127.299 680.264 125.749 681.519 123.938 682.258C122.127 682.996 120.136 683.184 118.218 682.798C116.299 682.413 114.539 681.47 113.159 680.091C111.78 678.711 110.844 676.956 110.469 675.048C110.094 673.14 110.298 671.164 111.054 669.371C111.81 667.578 113.085 666.047 114.717 664.974C116.349 663.9 118.265 663.331 120.223 663.34C121.53 663.311 122.831 663.547 124.044 664.033C125.257 664.519 126.357 665.245 127.279 666.166C128.201 667.088 128.924 668.187 129.405 669.395C129.886 670.603 130.114 671.896 130.076 673.194Z" />
              <path d="M133.998 458.746C133.683 458.73 133.37 458.785 133.078 458.903C132.786 459.02 132.523 459.196 132.305 459.419C132.087 459.647 131.92 459.917 131.815 460.21C131.71 460.504 131.669 460.817 131.695 461.127V463.484H138.408V462.196H136.3V461.432L138.324 460.214V458.687L136.063 460.144C135.909 459.725 135.627 459.365 135.257 459.114C134.886 458.863 134.446 458.734 133.998 458.746ZM135.016 461.127V462.196H132.965V461.127C132.943 460.986 132.955 460.837 132.998 460.7C133.041 460.563 133.115 460.434 133.214 460.328C133.313 460.222 133.435 460.14 133.57 460.085C133.706 460.034 133.852 460.011 133.998 460.019C134.141 460.019 134.283 460.046 134.414 460.101C134.545 460.156 134.663 460.238 134.76 460.344C134.856 460.449 134.929 460.575 134.973 460.708C135.018 460.845 135.032 460.986 135.016 461.127Z" />
              <path d="M128.82 461.295C128.82 462.532 129.19 463.746 129.883 464.776C130.576 465.806 131.561 466.609 132.714 467.083C133.866 467.556 135.135 467.682 136.358 467.439C137.582 467.196 138.706 466.601 139.589 465.724C140.471 464.847 141.071 463.731 141.315 462.517C141.558 461.303 141.433 460.042 140.956 458.899C140.479 457.751 139.67 456.772 138.633 456.087C137.595 455.398 136.376 455.03 135.128 455.03C133.455 455.03 131.85 455.692 130.667 456.866C129.484 458.041 128.82 459.635 128.82 461.295ZM140.138 461.295C140.141 462.282 139.848 463.249 139.297 464.071C138.746 464.894 137.962 465.536 137.043 465.916C136.125 466.296 135.113 466.393 134.137 466.202C133.161 466.01 132.265 465.536 131.561 464.835C130.858 464.138 130.379 463.249 130.186 462.278C129.992 461.311 130.093 460.304 130.475 459.392C130.857 458.48 131.503 457.7 132.331 457.152C133.16 456.608 134.133 456.314 135.128 456.318C136.456 456.322 137.728 456.847 138.667 457.779C139.605 458.711 140.134 459.975 140.138 461.295Z" />
              <path d="M139.915 699.03H84.3412V710.23H121.297L84.3412 736.648V748H139.915V736.814H102.973L139.915 710.396V699.03Z" />
            </g>
            {/* Labs */}
            <g fill={textColor} fillOpacity={0.6}>
              <path d="M126.972 303.634V291.808C129.243 291.573 130.731 288.832 130.731 284.76C130.731 282.567 130.104 279.826 128.303 279.826C126.11 279.826 125.719 282.332 123.526 290.163C121.49 297.525 117.966 302.459 111.387 302.459C103.869 302.459 99.3263 294.941 99.3263 286.796C99.3263 276.067 105.278 269.88 112.248 269.723V281.627C109.821 281.705 109.116 284.603 109.116 286.404C109.116 288.284 109.821 290.555 111.387 290.555C113.032 290.555 113.267 288.597 116.086 279.982C119.219 270.115 124.152 268 128.538 268C136.448 268 140.52 275.832 140.52 284.995C140.52 295.802 136.213 303.399 126.972 303.634Z" />
              <path d="M119.923 306.606C131.279 306.606 140.52 314.672 140.52 326.028C140.52 332.372 137.387 337.071 133.628 338.872L139.737 339.577V350.698H81V338.872H106.218C102.459 337.071 99.3259 332.372 99.3259 326.028C99.3259 314.672 108.567 306.606 119.923 306.606ZM119.923 318.51C114.832 318.51 110.447 322.347 110.447 328.064C110.447 333.86 114.832 337.541 119.923 337.541C125.014 337.541 129.321 333.86 129.321 328.064C129.321 322.347 125.014 318.51 119.923 318.51Z" />
              <path d="M119.923 401.834C108.568 401.834 99.3263 393.768 99.3263 382.412C99.3263 376.146 102.459 371.447 106.218 369.568L100.109 368.863V357.742H139.737V368.863L133.629 369.568C137.388 371.447 140.52 376.146 140.52 382.412C140.52 393.768 131.279 401.834 119.923 401.834ZM119.923 390.008C125.014 390.008 129.321 386.171 129.321 380.375C129.321 374.658 125.014 370.978 119.923 370.978C114.833 370.978 110.447 374.658 110.447 380.375C110.447 386.171 114.833 390.008 119.923 390.008Z" />
              <path d="M128.538 428.324V405.613H139.737V440.15H84.1328V428.324H128.538Z" />
            </g>
          </svg>
        </Box>
      )}
    </Box>
  );
};

export default Artwork;
